import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEditProfileModal } from "../../contexts/useEditProfileModal";
import { useUserStore, canEditSelectedProfile } from "../../store/user";
import logoImage from "../../assets/images/logo-georanking.png";
import {
  SidebarContainer,
  SidebarHeader,
  Logo,
  MenusWrapper,
  MenuSection,
  MenuTitle,
  MenuItem,
  MenuIcon,
  MenuLabel,
  MenuBadge,
  SidebarFooter,
  LogoutButton,
  MobileMenuButton,
  Overlay,
  UserSection,
  UserAvatar,
  UserInfo,
  UserName,
  UserEmail,
} from "./styles";

/** O perfil escolhido chega como "publicId - Nome"; mostramos só o nome. */
const nomeDoPerfil = (bruto?: string) => {
  const nome = bruto?.replace(/^[0-9a-f-]{36}\s*-\s*/i, "").trim();
  return nome || "";
};

const iniciais = (nome: string) =>
  nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase() || "GR";

interface SidebarProps {
  activeItem?: string;
}

export const Sidebar: React.FC<SidebarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useEditProfileModal();
  const { clearUser, user } = useUserStore();
  const canEditProfile = canEditSelectedProfile(user);
  const perfilAtual = nomeDoPerfil(user?.businessProfiles?.[0]);
  const [isOpen, setIsOpen] = useState(false);

  // Fecha o menu quando a rota muda
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Previne scroll do body quando o menu está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  interface MenuItem {
    id: string;
    label: string;
    icon: string;
    path: string;
    badge: null | string;
    isCustom?: boolean;
  }

  const menuItems: MenuItem[] = [
    {
      id: "home",
      label: "Visão geral",
      icon: "home",
      path: "/home",
      badge: null,
    },
    // Cliente com permissao VIEW no perfil selecionado nao ve "Editar Perfil".
    ...(canEditProfile
      ? [
          {
            id: "edit-profile",
            label: "Meus perfis",
            icon: "edit",
            path: "/edit-profile",
            badge: null,
            isCustom: true,
          },
        ]
      : []),
    {
      id: "georanking",
      label: "Novo perfil",
      icon: "add_location_alt",
      path: "/georanking",
      badge: null,
    },
    {
      id: "reviews",
      label: "Avaliações",
      icon: "reviews",
      path: "/reviews",
      badge: null,
    },
    {
      id: "posts",
      label: "Postagens",
      icon: "post_add",
      path: "/posts",
      badge: null,
    },
    {
      id: "reports-keywords",
      label: "Relatório de palavras-chave",
      icon: "query_stats",
      path: "/reports/keywords",
      badge: null,
    },
    {
      id: "reports-performance",
      label: "Desempenho",
      icon: "insights",
      path: "/reports/performance",
      badge: null,
    },
    ...(import.meta.env.VITE_BACKLINKS_DASHBOARD === "true"
      ? [
          {
            id: "backlinks-dashboard",
            label: "Backlinks",
            icon: "link",
            path: "/backlinks-dashboard",
            badge: null,
          },
        ]
      : []),
  ];

  const secondaryItems = [
    {
      id: "change-password",
      label: "Conta e segurança",
      icon: "lock",
      path: "/change-password",
    },
    {
      id: "help",
      label: "Central de ajuda",
      icon: "help",
      path: "/help",
    },
  ];

  const handleNavigation = (path: string, isCustom?: boolean, itemId?: string) => {
    if (isCustom && itemId === "edit-profile") {
      // Usa sempre o contexto global para abrir o modal
      openModal();
      setIsOpen(false);
      return;
    }
    
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = () => {
    clearUser();
    localStorage.removeItem("user-storage");
    navigate("/login");
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const isActive = (path: string) => {
    if (path === "/reports/keywords") {
      return location.pathname.startsWith("/reports/keywords");
    }
    if (path === "/reports/performance") {
      return location.pathname === "/reports/performance";
    }
    return location.pathname === path;
  };

  return (
    <>
      <MobileMenuButton onClick={toggleSidebar}>
        <span className="material-icons">
          {isOpen ? "close" : "menu"}
        </span>
      </MobileMenuButton>

      <Overlay $isOpen={isOpen} onClick={toggleSidebar} />

      <SidebarContainer $isOpen={isOpen}>
        <SidebarHeader>
          <Logo as="img" src={logoImage} alt="GeoRanking Logo" />
        </SidebarHeader>

        <MenusWrapper>
          <MenuSection aria-label="Menu do cliente">
            {menuItems.map((item) => (
              <MenuItem
                key={item.id}
                $active={isActive(item.path)}
                onClick={() => handleNavigation(item.path, item.isCustom, item.id)}
              >
                <MenuIcon className="material-icons">{item.icon}</MenuIcon>
                <MenuLabel>{item.label}</MenuLabel>
                {item.badge && <MenuBadge>{item.badge}</MenuBadge>}
              </MenuItem>
            ))}
          </MenuSection>

          <MenuSection aria-label="Conta">
            <MenuTitle>Conta</MenuTitle>
            {secondaryItems.map((item) => (
              <MenuItem
                key={item.id}
                $active={isActive(item.path)}
                onClick={() => handleNavigation(item.path)}
              >
                <MenuIcon className="material-icons">{item.icon}</MenuIcon>
                <MenuLabel>{item.label}</MenuLabel>
              </MenuItem>
            ))}
          </MenuSection>
        </MenusWrapper>

        <SidebarFooter>
          <UserSection>
            <UserAvatar aria-hidden="true">{iniciais(perfilAtual || user?.name || "")}</UserAvatar>
            <UserInfo>
              <UserName>{perfilAtual || user?.name || "Cliente"}</UserName>
              <UserEmail>{perfilAtual ? "Cliente" : "GeoRanking"}</UserEmail>
            </UserInfo>
            <LogoutButton type="button" onClick={handleLogout} aria-label="Sair" title="Sair">
              <span className="material-icons">logout</span>
            </LogoutButton>
          </UserSection>
        </SidebarFooter>
      </SidebarContainer>
    </>
  );
};
